/**
 * entrega-estimada-producto.js
 *
 * Añadir en theme.liquid con:
 *   <script src="{{ 'entrega-estimada-producto.js' | asset_url }}" defer></script>
 *
 * Requiere que el snippet entrega-estimada-producto.liquid esté presente en
 * la página de producto. Vigila el cambio de variante por sondeo (polling)
 * del input oculto "id" del formulario de compra, porque el atributo HTML
 * value de un <input> no refleja los cambios hechos por JS (el tema los
 * hace así), así que un MutationObserver no lo detectaría de forma fiable.
 */
(function () {
  let festivosCache = null;
  let festivosPromesaEnCurso = null;
  let ultimaVarianteVista = null;
  let intervaloTick = null;

  // La hora de corte es la hora del almacén (Madrid), no la del navegador
  // del cliente — si alguien compra desde Nueva York (6h de diferencia),
  // comparar contra su hora local daría un resultado completamente
  // distinto al real. Se construye un Date cuyos campos (horas, minutos,
  // día...) reflejan la hora de Madrid, usando Intl.DateTimeFormat con esa
  // zona horaria explícita — el desfase de interpretación que introduce
  // "new Date(string sin TZ)" es sistemático e igual en todas las fechas
  // que se calculan así, así que las restas entre ellas (ej. cuánto falta
  // para el corte) siguen siendo correctas.
  function ahoraEnMadrid() {
    const partes = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Madrid",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const obj = {};
    partes.forEach((p) => (obj[p.type] = p.value));
    // hour12:false a veces da "24" en vez de "00" para medianoche en
    // algunos motores — se normaliza por si acaso.
    const hora = obj.hour === "24" ? "00" : obj.hour;
    return new Date(`${obj.year}-${obj.month}-${obj.day}T${hora}:${obj.minute}:${obj.second}`);
  }

  function getContenedor() {
    return document.querySelector("[data-entrega-estimada-producto]");
  }

  async function cargarFestivos(url) {
    if (festivosCache) return festivosCache;
    if (festivosPromesaEnCurso) return festivosPromesaEnCurso;

    festivosPromesaEnCurso = (async () => {
      try {
        const res = await fetch(url);
        const data = await res.json();
        const todos = [
          ...(data.nacionales || []),
          ...(data.regionales_andalucia || []),
          ...(data.locales_chiclana_de_la_frontera || []),
        ];
        festivosCache = new Set(todos);
      } catch (e) {
        console.error("entrega-estimada-producto: no se pudieron cargar los festivos", e);
        festivosCache = new Set();
      }
      return festivosCache;
    })();

    return festivosPromesaEnCurso;
  }

  function esFinDeSemana(date) {
    const d = date.getDay();
    return d === 0 || d === 6;
  }

  function toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function sumarDiasLaborables(fechaInicio, diasLaborables, festivos) {
    const fecha = new Date(fechaInicio);
    let restantes = diasLaborables;
    while (restantes > 0) {
      fecha.setDate(fecha.getDate() + 1);
      if (esFinDeSemana(fecha)) continue;
      if (festivos.has(toISO(fecha))) continue;
      restantes -= 1;
    }
    return fecha;
  }

  function formatearFecha(date, locale) {
    // no year and no comma: "miércoles 11 de noviembre" / "Wednesday 11 November"
    return new Intl.DateTimeFormat(locale || "es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(date).replace(",", "");
  }

  function formatearTiempoRestante(ms, locale) {
    const totalSegundos = Math.max(0, Math.floor(ms / 1000));
    const horas = Math.floor(totalSegundos / 3600);
    const minutos = Math.floor((totalSegundos % 3600) / 60);
    const segundos = totalSegundos % 60;
    const esIngles = (locale || "").indexOf("en") === 0;
    const etiquetaH = "h";
    const etiquetaMin = "min";
    const etiquetaSeg = esIngles ? "s" : "s";
    if (horas > 0) {
      return `${horas} ${etiquetaH} ${minutos} ${etiquetaMin}`;
    }
    if (minutos > 0) return `${minutos}${etiquetaMin}`;
    return `${segundos}${etiquetaSeg}`;
  }

  // Devuelve la fecha base sobre la que se cuentan los días laborables del
  // producto: hoy si aún no ha pasado la hora de corte, o el siguiente día
  // laborable si ya ha pasado. msHastaCorte es siempre el tiempo hasta el
  // PRÓXIMO corte diario (el de hoy si aún no ha pasado, o el de mañana si
  // ya pasó) — nunca null, para que la cuenta atrás se muestre siempre.
  function esLaborable(fecha, festivos) {
    return !esFinDeSemana(fecha) && !festivos.has(toISO(fecha));
  }

  // Sábados, domingos y festivos no se prepara ningún pedido: aunque sea
  // antes de la hora de corte, se cuenta desde el siguiente día laborable,
  // y la cuenta atrás apunta al corte de ese día.
  function calcularBase(hoy, horaCorte, festivos) {
    const corteHoy = new Date(hoy);
    corteHoy.setHours(horaCorte, 0, 0, 0);

    if (esLaborable(hoy, festivos) && hoy < corteHoy) {
      return { base: new Date(hoy), msHastaCorte: corteHoy - hoy };
    }

    const siguienteLaborable = sumarDiasLaborables(hoy, 1, festivos);
    const corteSiguiente = new Date(siguienteLaborable);
    corteSiguiente.setHours(horaCorte, 0, 0, 0);
    return { base: siguienteLaborable, msHastaCorte: corteSiguiente - hoy };
  }

  function leerVariantes(contenedor) {
    try {
      return JSON.parse(contenedor.getAttribute("data-variantes") || "{}");
    } catch (e) {
      console.error("entrega-estimada-producto: data-variantes no es JSON válido", e);
      return {};
    }
  }

  function leerDisponibilidad(contenedor) {
    try {
      return JSON.parse(contenedor.getAttribute("data-disponibilidad") || "{}");
    } catch (e) {
      console.error("entrega-estimada-producto: data-disponibilidad no es JSON válido", e);
      return {};
    }
  }

  function obtenerVarianteActual(contenedor) {
    // 1) el input oculto del formulario de compra es la fuente más fiable,
    //    porque refleja la variante seleccionada de verdad en el momento
    //    de añadir al pedido (por si hubiera varios formularios en la
    //    página, tomamos el que apunte a /cart/add).
    const inputId = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
    );
    if (inputId && inputId.value) return inputId.value;

    // 2) fallback: el valor inicial que dejó el snippet Liquid en el
    //    render del servidor (product.selected_or_first_available_variant).
    return contenedor.getAttribute("data-variante-actual");
  }

  async function tick() {
    const contenedor = getContenedor();
    if (!contenedor) {
      if (intervaloTick) clearInterval(intervaloTick);
      return;
    }

    const variantes = leerVariantes(contenedor);
    const disponibilidad = leerDisponibilidad(contenedor);
    const varianteActual = obtenerVarianteActual(contenedor);
    const dias = parseInt(variantes[varianteActual], 10);

    const mensaje = contenedor.querySelector("[data-mensaje]");

    // Si la variante seleccionada está agotada, no tiene sentido prometer
    // una fecha de entrega de algo que no se puede comprar ahora mismo.
    // disponibilidad[varianteActual] === false de forma explícita (no solo
    // "falsy") para no ocultar el bloque por error si el dato faltara.
    if (disponibilidad[varianteActual] === false) {
      if (mensaje) mensaje.hidden = true;
      return;
    }

    if (isNaN(dias)) {
      if (mensaje) mensaje.hidden = true;
      return;
    }

    const horaCorte = parseInt(contenedor.getAttribute("data-hora-corte"), 10) || 12;
    const locale = contenedor.getAttribute("data-locale");
    const festivosUrl = contenedor.getAttribute("data-festivos-url");
    const festivos = await cargarFestivos(festivosUrl);

    const ahora = ahoraEnMadrid();
    const { base, msHastaCorte } = calcularBase(ahora, horaCorte, festivos);
    const fechaEntrega = sumarDiasLaborables(base, dias, festivos);
    const textoFecha = formatearFecha(fechaEntrega, locale);

    const fechaEl = contenedor.querySelector("[data-fecha]");
    const cuentaAtras = contenedor.querySelector("[data-cuenta-atras]");
    const tiempoRestanteEl = contenedor.querySelector("[data-tiempo-restante]");

    if (fechaEl && fechaEl.textContent !== textoFecha) fechaEl.textContent = textoFecha;
    if (mensaje) mensaje.hidden = false;

    if (msHastaCorte > 0 && msHastaCorte <= 24 * 3600 * 1000) {
      if (cuentaAtras) cuentaAtras.hidden = false;
      if (tiempoRestanteEl) {
        tiempoRestanteEl.textContent = formatearTiempoRestante(msHastaCorte, locale);
      }
    } else if (cuentaAtras) {
      cuentaAtras.hidden = true;
    }
  }

  function iniciar() {
    const contenedor = getContenedor();
    if (!contenedor) return;

    tick();

    // Cuenta atrás en vivo + detección de cambio de variante por sondeo.
    // Un intervalo de 1s es suficiente para la cuenta atrás y barato para
    // comprobar a la vez si la variante seleccionada ha cambiado.
    if (intervaloTick) clearInterval(intervaloTick);
    intervaloTick = setInterval(() => {
      const actual = obtenerVarianteActual(contenedor);
      if (actual !== ultimaVarianteVista) {
        ultimaVarianteVista = actual;
      }
      tick();
    }, 1000);

    // Recalcula también al recuperar el foco de la pestaña (carrito viejo
    // abierto de un día para otro cruzando la hora de corte o el propio día).
    window.addEventListener("pageshow", tick);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") tick();
    });
  }

  document.addEventListener("DOMContentLoaded", iniciar);

  window.EntregaEstimadaProducto = { refresh: tick };
})();